"use client"

import { useState, useEffect, useRef, useCallback } from "react"
import { X, Save, Bold, Italic, Underline, List, ListOrdered, AlignLeft, AlignCenter, AlignRight, Image, Link } from "lucide-react"

interface NotesEditorModalProps {
  isOpen: boolean
  onClose: () => void
  title: string
  lessonId: string
}

export function NotesEditorModal({ isOpen, onClose, title, lessonId }: NotesEditorModalProps) {
  const [content, setContent] = useState("")
  const [isSaving, setIsSaving] = useState(false)
  const [lastSaved, setLastSaved] = useState<Date | null>(null)
  const [isMobile, setIsMobile] = useState(false)

  const editorRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const modalRef = useRef<HTMLDivElement>(null)

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }
    
    checkMobile()
    window.addEventListener('resize', checkMobile)
    
    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Load notes from localStorage
  useEffect(() => {
    if (isOpen) {
      const savedNotes = localStorage.getItem(`lesson-notes-${lessonId}`)
      const initial = savedNotes || "<p style='color: #000000;'>Start taking your notes here...</p>"
      setContent(initial)

      // Insert content into editor DOM (ONLY once when opening)
      if (editorRef.current) {
        editorRef.current.innerHTML = initial
      }

      setLastSaved(new Date())
    }
  }, [isOpen, lessonId])

  // Auto-save
  useEffect(() => {
    if (!isOpen || !content) return

    const timer = setTimeout(() => {
      handleSave()
    }, 2000)

    return () => clearTimeout(timer)
  }, [content, isOpen])

  // Click outside + ESC close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        handleSave()
        onClose()
      }
    }

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleSave()
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscapeKey)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscapeKey)
    }
  }, [isOpen, onClose])

  // Stop body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = "unset"
    }

    return () => {
      document.body.style.overflow = "unset"
    }
  }, [isOpen])

  const handleSave = useCallback(async () => {
    if (!content) return

    setIsSaving(true)
    await new Promise(res => setTimeout(res, 500))

    try {
      localStorage.setItem(`lesson-notes-${lessonId}`, content)
      setLastSaved(new Date())
    } catch (error) {
      console.error("Failed to save notes:", error)
    } finally {
      setIsSaving(false)
    }
  }, [content, lessonId])

  const handleInput = () => {
    if (editorRef.current) {
      setContent(editorRef.current.innerHTML)
    }
  }

  const formatText = (command: string, value: string = "") => {
    document.execCommand(command, false, value)
    handleInput()
    editorRef.current?.focus()
  }

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith("image/")) {
      alert("Please upload an image file")
      return
    }

    const reader = new FileReader()
    reader.onload = e => {
      const url = e.target?.result as string
      formatText("insertImage", url)
    }
    reader.readAsDataURL(file)

    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  const insertLink = () => {
    const url = prompt("Enter URL:")
    if (url) formatText("createLink", url)
  }

  const clearNotes = () => {
    if (confirm("Clear all notes? This cannot be undone.")) {
      const initial = "<p style='color: #000000;'>Start taking your notes here...</p>"
      setContent(initial)

      if (editorRef.current) {
        editorRef.current.innerHTML = initial
      }

      localStorage.removeItem(`lesson-notes-${lessonId}`)
      setLastSaved(new Date())
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.ctrlKey && e.key === "s") {
      e.preventDefault()
      handleSave()
    }
  }

  if (!isOpen) return null

  return (

    
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
      <div 
        className="absolute inset-0 bg-black/30 backdrop-blur-sm transition-opacity duration-300"
        onClick={onClose}
      />

      <div
        ref={modalRef}
        className="bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-4xl h-[95vh] sm:h-[90vh] flex flex-col relative z-10"
      >
        {/* Header - Optimized for mobile */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-slate-200">
          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
            <button
              onClick={() => {
                handleSave()
                onClose()
              }}
              className="p-2 hover:bg-slate-100 rounded-lg flex-shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center"
            >
              <X className="w-5 h-5 text-slate-600" />
            </button>

            <div className="min-w-0 flex-1">
              <h2 className="text-lg sm:text-xl font-semibold text-slate-900 truncate">{title}</h2>
              <p className="text-xs sm:text-sm text-slate-500 truncate">
                {lastSaved && `Saved: ${lastSaved.toLocaleTimeString()}`}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0 ml-2">
            <button
              onClick={clearNotes}
              className="px-3 sm:px-4 py-2 text-red-600 bg-red-50 rounded-lg hover:bg-red-100 text-sm font-medium min-h-[44px]"
            >
              {isMobile ? "Clear" : "Clear Notes"}
            </button>

            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium min-h-[44px]"
            >
              <Save className="w-4 h-4 flex-shrink-0" />
              {isMobile ? (isSaving ? "..." : "Save") : (isSaving ? "Saving..." : "Save")}
            </button>
          </div>
        </div>

        {/* Toolbar - Optimized for mobile */}
        <div className="flex items-center gap-1 p-2 sm:p-4 border-b border-slate-200 bg-slate-50 overflow-x-auto">
          {/* Text Formatting */}
          <div className="flex items-center gap-1 border-r border-slate-300 pr-2 sm:pr-3 mr-2 sm:mr-3 flex-shrink-0">
            <button 
              onClick={() => formatText("bold")} 
              className="p-2 hover:bg-white rounded transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
              title="Bold"
            >
              <Bold className="w-4 h-4 text-gray-900" />
            </button>
            <button 
              onClick={() => formatText("italic")} 
              className="p-2 hover:bg-white rounded transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
              title="Italic"
            >
              <Italic className="w-4 h-4 text-gray-900" />
            </button>
            <button 
              onClick={() => formatText("underline")} 
              className="p-2 hover:bg-white rounded transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
              title="Underline"
            >
              <Underline className="w-4 h-4 text-gray-900" />
            </button>
          </div>

          {/* Alignment - Hide on very small screens */}
          {!isMobile && (
            <div className="flex items-center gap-1 border-r border-slate-300 pr-2 sm:pr-3 mr-2 sm:mr-3 flex-shrink-0">
              <button 
                onClick={() => formatText("justifyLeft")} 
                className="p-2 hover:bg-white rounded transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                title="Align Left"
              >
                <AlignLeft className="w-4 h-4 text-gray-900" />
              </button>
              <button 
                onClick={() => formatText("justifyCenter")} 
                className="p-2 hover:bg-white rounded transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                title="Align Center"
              >
                <AlignCenter className="w-4 h-4 text-gray-900" />
              </button>
              <button 
                onClick={() => formatText("justifyRight")} 
                className="p-2 hover:bg-white rounded transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
                title="Align Right"
              >
                <AlignRight className="w-4 h-4 text-gray-900" />
              </button>
            </div>
          )}

          {/* Lists */}
          <div className="flex items-center gap-1 border-r border-slate-300 pr-2 sm:pr-3 mr-2 sm:mr-3 flex-shrink-0">
            <button 
              onClick={() => formatText("insertUnorderedList")} 
              className="p-2 hover:bg-white rounded transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
              title="Bullet List"
            >
              <List className="w-4 h-4 text-gray-900" />
            </button>
            <button 
              onClick={() => formatText("insertOrderedList")} 
              className="p-2 hover:bg-white rounded transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
              title="Numbered List"
            >
              <ListOrdered className="w-4 h-4 text-gray-900" />
            </button>
          </div>

          {/* Media */}
          <div className="flex items-center gap-1 flex-shrink-0">
            <input type="file" ref={fileInputRef} onChange={handleImageUpload} accept="image/*" className="hidden" />
            <button 
              onClick={() => fileInputRef.current?.click()} 
              className="p-2 hover:bg-white rounded transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
              title="Insert Image"
            >
              <Image className="w-4 h-4 text-gray-900" />
            </button>
            <button 
              onClick={insertLink} 
              className="p-2 hover:bg-white rounded transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
              title="Insert Link"
            >
              <Link className="w-4 h-4 text-gray-900" />
            </button>
          </div>
        </div>

        {/* Editor - Optimized for mobile */}
        <div className="flex-1 overflow-auto p-3 sm:p-6">
          <div
            ref={editorRef}
            contentEditable
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            className="w-full h-full min-h-[300px] sm:min-h-[400px] p-4 sm:p-6 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            style={{ 
              lineHeight: "1.6",
              color: "#000000",
              fontSize: isMobile ? "16px" : "inherit" // Prevents zoom on iOS
            }}
          />
        </div>

        {/* Footer - Optimized for mobile */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 border-t border-slate-200 bg-slate-50 gap-2">
          <p className="text-xs sm:text-sm text-slate-500 text-center sm:text-left">
            {isMobile ? "💡 Ctrl+S to save" : "💡 Use Ctrl+S to save • Images stored locally"}
          </p>
          <p className="text-xs sm:text-sm text-slate-500 text-center sm:text-right">
            {isSaving ? <span className="text-blue-600">Saving...</span> : "Auto-save enabled"}
          </p>
        </div>
      </div>
    </div>
  )
}